import React, { useState, useEffect, useCallback, useRef } from 'react';
import { usePharmacyStore } from '../../stores/pharmacyStore';
import { humanizeAttachmentLabel } from '../../utils/attachmentLabel';
import {
  Box,
  Stack,
  Typography,
  Chip,
  Tabs,
  Tab,
  Alert,
  Button,
  IconButton,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Image as ImageIcon,
  Visibility as VisibilityIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Refresh as RefreshIcon,
  RotateRight as RotateRightIcon,
  Flip as FlipIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { rxService, RxPrescription, RxImage } from '../../services/rxService';
import { ImageConverter } from '../patient-360/rx/utils/imageConverter';
import { RxAdjStateBadge, RxStatusBadge } from './RxStatusBadge';
import { useImagePan } from './useImagePan';

/**
 * Minimal medication shape the panel needs. Both
 * `MedicationData` (clinical) and `ProductMedicationData` (product) satisfy
 * this, so callers can pass their own medication arrays directly.
 */
export interface RxImagePanelMedication {
  rxNumber: string;
  status?: number; // Rx.Status code from Kroll (1-8) — drives the RxStatusBadge
  rxAdjState?: number | null; // Kroll Rx.AdjState (0/1/5/6/7 = non-clean adjudication; see RxAdjStateBadge)
}

/**
 * Per-drug image state. Mirrors the engine that previously lived inline in
 * ClinicalVerificationDialog: each drug index caches its loaded attachments,
 * the active attachment's image/url, and multi-page TIFF state.
 */
interface DrugImageState {
  /** The Rx# this entry was fetched for — used to detect index reshuffles. */
  rxNumber: string;
  prescription: RxPrescription | null;
  image: RxImage | null;
  imageUrl: string | null;
  tiffUrls: string[];
  currentPage: number;
  loading: boolean;
  error: string | null;
  allPrescriptions: RxPrescription[];
  currentAttachmentIndex: number;
  /** Fillware .zip→PDF scans render in an <iframe>, not the raster <img>. */
  isPdf?: boolean;
}

export interface RxImagePanelProps {
  /** Patient whose Rx attachments are fetched. */
  patientId: string | undefined;
  /** Pharmacy context for the Kroll-backed fetch. */
  pharmacyId: string | undefined;
  /** Ordered list of medications/drugs shown by the parent dialog. */
  medications: RxImagePanelMedication[];
  /** Which medication index is currently active (controlled by the parent). */
  activeDrugIndex: number;
  /**
   * Optional callback for the "Full View" button. When provided, the panel
   * surfaces the button and hands back the active attachment + image so the
   * parent can open its own RxImageModal.
   */
  onViewFullImage?: (prescription: RxPrescription, image: RxImage) => void;
  /**
   * Optional extra controls rendered in the image toolbar (e.g. PV's
   * print-receipt button). Rendered to the left of the zoom controls.
   */
  toolbarActions?: React.ReactNode;
  /**
   * Optional extra controls rendered in the panel header (right side, next to
   * the "N files" chip).
   */
  headerActions?: React.ReactNode;
  /**
   * Optional key (e.g. orderId). When it changes, the internal per-drug image
   * cache is cleared so a reused dialog never shows a prior order's images.
   */
  resetKey?: string | number;
  /**
   * Optional per-drug cache pre-warm, awaited (non-blocking) right before the
   * active drug's attachments are fetched. Lets the host refresh ONLY the drug
   * being viewed instead of bulk-warming every Rx in the order on open — so a
   * drug's script image loads when you click to it, not upfront. A rejection
   * is swallowed (the fetch proceeds against whatever the cache already has).
   */
  prewarmActiveRx?: (rxNumber: string) => Promise<void>;
  /**
   * How a (non-PDF) image is fitted to the viewer. 'width' (default) scales the
   * image to the container width — the Clinical / Product Verification behavior.
   * 'height' scales it to the container height so a full-page script fits without
   * vertical scrolling (used by the full-screen Prescription Images viewer).
   */
  fitMode?: 'width' | 'height';
}

const getAttachmentTypeLabel = (scriptImageType?: number): string => {
  switch (scriptImageType) {
    case 1:
      return 'Script Image';
    case 2:
      return 'Hard Copy';
    default:
      return 'Document';
  }
};

/**
 * Shared Rx-image viewer. Encapsulates the per-drug fetch + caching engine,
 * attachment tabs (Script Image / Hard Copy), multi-page TIFF navigation, and
 * zoom / rotate / flip controls. Used by both the Clinical Verification and
 * Product Verification dialogs.
 */
const RxImagePanel: React.FC<RxImagePanelProps> = ({
  patientId,
  pharmacyId,
  medications,
  activeDrugIndex,
  onViewFullImage,
  toolbarActions,
  headerActions,
  resetKey,
  prewarmActiveRx,
  fitMode = 'width',
}) => {
  // Per-drug image cache, keyed by drug index (matches Clinical's prior engine).
  // PRX-30: Fillware attachments carry their kind as a scan-time comment;
  // Kroll's `description` is free text, so only Fillware pharmacies label from it.
  const labelsFromComments = usePharmacyStore((s) => s.activePharmacy?.databaseType === 'fillware');
  const [drugImages, setDrugImages] = useState<Map<number, DrugImageState>>(new Map());

  // Revoke any object URLs we created when the panel unmounts so blob: URLs for
  // images/PDFs (PDFs can be multi-MB) don't leak for the whole browser session
  // — mirrors the cleanup RxImageThumbnail does. A ref tracks the latest cache so
  // the unmount handler sees every entry without re-subscribing on each change.
  const drugImagesRef = useRef(drugImages);
  drugImagesRef.current = drugImages;
  useEffect(() => {
    return () => {
      drugImagesRef.current.forEach((st) => {
        if (st?.imageUrl?.startsWith('blob:')) URL.revokeObjectURL(st.imageUrl);
        st?.tiffUrls?.forEach((u) => {
          if (u?.startsWith('blob:')) URL.revokeObjectURL(u);
        });
      });
    };
  }, []);

  // View transform state — applied to the active image.
  const [zoom, setZoom] = useState(100); // percentage
  const [rotation, setRotation] = useState(0); // degrees, 90° increments
  const [flipped, setFlipped] = useState(false); // horizontal flip toggle

  // Grab-to-pan + scroll for the zoomed image (the image stays fully reachable
  // because it's centered with `margin: auto`, which collapses to start-align on
  // overflow — see useImagePan).
  const pan = useImagePan();

  const currentMedication = medications[activeDrugIndex];
  const currentDrugImage = drugImages.get(activeDrugIndex);
  // PDFs (e.g. Rx-extension hard copies from Kroll, or Fillware PDF scans —
  // flagged as `currentDrugImage.isPdf` at build time) can't render in an
  // <img> — they're shown in an <iframe> via the browser's built-in PDF
  // viewer instead.
  const isPdf =
    currentDrugImage?.isPdf === true ||
    (currentDrugImage?.image?.mimeType || '').toLowerCase() === 'application/pdf';

  // The raw image source for the active drug. Rotation/flip is baked into a
  // canvas-derived URL (see effect below); `displayUrl` is what the <img> renders.
  const sourceUrl = currentDrugImage?.imageUrl ?? null;
  const [displayUrl, setDisplayUrl] = useState<string | null>(null);
  const genUrlRef = useRef<string | null>(null);

  const fetchDrugRxImage = useCallback(
    async (drugIndex: number, rxNumber: string) => {
      if (!patientId || !pharmacyId || !rxNumber) return;

      // Show loading for this drug.
      setDrugImages((prev) => {
        const updated = new Map(prev);
        updated.set(drugIndex, {
          rxNumber: String(rxNumber),
          prescription: null,
          image: null,
          imageUrl: null,
          tiffUrls: [],
          currentPage: 0,
          loading: true,
          error: null,
          allPrescriptions: [],
          currentAttachmentIndex: 0,
        });
        return updated;
      });

      try {
        // Lazily warm the cache for THIS drug only (no-op if host didn't pass a
        // prewarm). Non-blocking: on failure we still fetch whatever is cached.
        if (prewarmActiveRx) {
          try {
            await prewarmActiveRx(String(rxNumber));
          } catch (prewarmError) {
            console.warn(`Script image pre-warm failed for Rx #${rxNumber} (non-blocking):`, prewarmError);
          }
        }

        // Fetch ALL attachments (Script Images + Hard Copies) for this Rx.
        // GP-1171: a fetch FAILURE (Kroll slow/gateway timeout) must read
        // differently from a genuine empty result — the tech needs to know a
        // retry can help, not conclude the Rx has no image.
        let allAttachments: Awaited<ReturnType<typeof rxService.getAllRxAttachments>>;
        try {
          allAttachments = await rxService.getAllRxAttachments(
            pharmacyId,
            patientId,
            String(rxNumber),
          );
        } catch (attachmentsError) {
          console.error(`Failed to fetch attachments for Rx #${rxNumber}:`, attachmentsError);
          setDrugImages((prev) => {
            const updated = new Map(prev);
            updated.set(drugIndex, {
              rxNumber: String(rxNumber),
              prescription: null,
              image: null,
              imageUrl: null,
              tiffUrls: [],
              currentPage: 0,
              loading: false,
              error: `Pharmacy system is responding slowly — attachments for Rx #${rxNumber} couldn't be loaded. Retry in a moment.`,
              allPrescriptions: [],
              currentAttachmentIndex: 0,
            });
            return updated;
          });
          return;
        }

        if (allAttachments.length > 0) {
          // Default to the first attachment (usually Script Image; sorted by type ASC).
          const firstAttachment = allAttachments[0];

          if (!firstAttachment.scriptImageContentId) {
            setDrugImages((prev) => {
              const updated = new Map(prev);
              updated.set(drugIndex, {
                rxNumber: String(rxNumber),
                prescription: firstAttachment,
                image: null,
                imageUrl: null,
                tiffUrls: [],
                currentPage: 0,
                loading: false,
                error: `No script image available for Rx #${rxNumber}`,
                allPrescriptions: allAttachments,
                currentAttachmentIndex: 0,
              });
              return updated;
            });
            return;
          }

          const image = await rxService.getRxImage(
            pharmacyId,
            patientId,
            firstAttachment.scriptImageContentId,
          );

          // Fillware delivers script scans as .zip→PDF. A PDF can't go through
          // the raster ImageConverter (it throws on a non-image payload); render
          // it straight in an <iframe> via a data URL instead.
          const isPdf = (image.mimeType || '').toLowerCase().includes('pdf');
          if (isPdf) {
            setDrugImages((prev) => {
              const updated = new Map(prev);
              updated.set(drugIndex, {
                rxNumber: String(rxNumber),
                prescription: firstAttachment,
                image,
                imageUrl: `data:application/pdf;base64,${image.content}`,
                tiffUrls: [],
                currentPage: 0,
                loading: false,
                error: null,
                allPrescriptions: allAttachments,
                currentAttachmentIndex: 0,
                isPdf: true,
              });
              return updated;
            });
            return;
          }

          try {
            const prepared = await ImageConverter.prepareImageForDisplay(
              image.content,
              image.mimeType,
              image.compressionType,
            );

            let imageUrl: string | null = null;
            let tiffUrls: string[] = [];

            if (prepared.tiffUrls && prepared.tiffUrls.length > 0) {
              tiffUrls = prepared.tiffUrls;
              imageUrl = prepared.tiffUrls[0];
            } else {
              // PDFs render in an <iframe>; blob URLs are far more reliable than
              // multi-MB data URLs for PDF rendering across browsers.
              const preferBlob = (image.mimeType || '').toLowerCase() === 'application/pdf';
              imageUrl = preferBlob
                ? (prepared.blobUrl || prepared.dataUrl || null)
                : (prepared.dataUrl || prepared.blobUrl || null);
            }

            setDrugImages((prev) => {
              const updated = new Map(prev);
              updated.set(drugIndex, {
                rxNumber: String(rxNumber),
                prescription: firstAttachment,
                image,
                imageUrl,
                tiffUrls,
                currentPage: 0,
                loading: false,
                error: null,
                allPrescriptions: allAttachments,
                currentAttachmentIndex: 0,
                isPdf: false,
              });
              return updated;
            });
          } catch (conversionError) {
            console.error('Failed to convert image:', conversionError);
            // Fallback to direct display.
            const fallbackUrl = `data:${image.mimeType};base64,${image.content}`;
            setDrugImages((prev) => {
              const updated = new Map(prev);
              updated.set(drugIndex, {
                rxNumber: String(rxNumber),
                prescription: firstAttachment,
                image,
                imageUrl: fallbackUrl,
                tiffUrls: [],
                currentPage: 0,
                loading: false,
                error: null,
                allPrescriptions: allAttachments,
                currentAttachmentIndex: 0,
              });
              return updated;
            });
          }
        } else {
          setDrugImages((prev) => {
            const updated = new Map(prev);
            updated.set(drugIndex, {
              rxNumber: String(rxNumber),
              prescription: null,
              image: null,
              imageUrl: null,
              tiffUrls: [],
              currentPage: 0,
              loading: false,
              error: `No script image found for Rx #${rxNumber}`,
              allPrescriptions: [],
              currentAttachmentIndex: 0,
            });
            return updated;
          });
        }
      } catch (error) {
        console.error(`Failed to fetch RX image for Rx #${rxNumber}:`, error);
        setDrugImages((prev) => {
          const updated = new Map(prev);
          updated.set(drugIndex, {
            rxNumber: String(rxNumber),
            prescription: null,
            image: null,
            imageUrl: null,
            tiffUrls: [],
            currentPage: 0,
            loading: false,
            error: 'Failed to load prescription image',
            allPrescriptions: [],
            currentAttachmentIndex: 0,
          });
          return updated;
        });
      }
    },
    [patientId, pharmacyId, prewarmActiveRx],
  );

  // Fetch the active drug's image whenever it changes (and isn't cached yet).
  // The cache is keyed by drug index, but indices can reshuffle after a split
  // (a drug is removed and the array re-indexes). Detect that by comparing the
  // cached entry's Rx# against the current medication's Rx#; on mismatch we
  // re-fetch so the panel never shows a stale drug's image at a reused index.
  useEffect(() => {
    if (!patientId || !pharmacyId || !currentMedication?.rxNumber) return;
    const cached = drugImages.get(activeDrugIndex);
    // Keep the cached entry only when it was fetched for the current Rx#.
    if (cached && cached.rxNumber === String(currentMedication.rxNumber)) return;
    fetchDrugRxImage(activeDrugIndex, currentMedication.rxNumber);
  }, [
    patientId,
    pharmacyId,
    activeDrugIndex,
    currentMedication?.rxNumber,
    drugImages,
    fetchDrugRxImage,
  ]);

  // Clear the per-drug cache when the host order changes so a reused dialog
  // never shows a prior order's images.
  useEffect(() => {
    setDrugImages(new Map());
  }, [resetKey]);

  // Reset the view transform whenever the displayed attachment/drug changes.
  useEffect(() => {
    setZoom(100);
    setRotation(0);
    setFlipped(false);
  }, [activeDrugIndex, currentDrugImage?.currentAttachmentIndex]);

  // Re-measure pan availability after any layout-affecting view change. Zoom
  // changes the image width but not the (flex:1) container, so the ResizeObserver
  // inside the hook won't catch it — refresh explicitly.
  useEffect(() => {
    pan.refresh();
  }, [zoom, rotation, flipped, displayUrl, pan.refresh]);

  // GP-909: bake rotation/flip into the bitmap on a canvas so the displayed image
  // is genuinely re-oriented, rather than using a CSS `transform: rotate()`. CSS
  // transforms don't affect scroll/layout, so a rotated image's left edge fell
  // into negative scroll space the container could never reach. With the pixels
  // actually rotated, the proven zoom + `margin:auto` + pan path keeps every edge
  // reachable in landscape too. At 0°/no-flip the source passes straight through
  // (no re-encode) — identical to the prior, well-liked behavior. PDFs render in
  // an <iframe> (rotate controls hidden), so they skip this path.
  useEffect(() => {
    if (isPdf || !sourceUrl || (rotation === 0 && !flipped)) {
      setDisplayUrl(sourceUrl);
      return;
    }

    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      try {
        const swap = rotation === 90 || rotation === 270;
        const canvas = document.createElement('canvas');
        canvas.width = swap ? img.naturalHeight : img.naturalWidth;
        canvas.height = swap ? img.naturalWidth : img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          setDisplayUrl(sourceUrl);
          return;
        }
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((rotation * Math.PI) / 180);
        ctx.scale(flipped ? -1 : 1, 1);
        ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
        canvas.toBlob((blob) => {
          if (cancelled) return;
          if (!blob) {
            setDisplayUrl(sourceUrl);
            return;
          }
          const url = URL.createObjectURL(blob);
          if (genUrlRef.current) URL.revokeObjectURL(genUrlRef.current);
          genUrlRef.current = url;
          setDisplayUrl(url);
        }, 'image/png');
      } catch {
        if (!cancelled) setDisplayUrl(sourceUrl);
      }
    };
    img.onerror = () => {
      if (!cancelled) setDisplayUrl(sourceUrl);
    };
    img.src = sourceUrl;

    return () => {
      cancelled = true;
    };
  }, [sourceUrl, rotation, flipped, isPdf]);

  // Free the last canvas-generated blob URL on unmount.
  useEffect(() => {
    return () => {
      if (genUrlRef.current) URL.revokeObjectURL(genUrlRef.current);
    };
  }, []);

  const handleTiffPageChange = (page: number) => {
    if (!currentDrugImage || currentDrugImage.tiffUrls.length === 0) return;
    if (page < 0 || page >= currentDrugImage.tiffUrls.length) return;
    setDrugImages((prev) => {
      const updated = new Map(prev);
      const existing = prev.get(activeDrugIndex);
      if (existing) {
        updated.set(activeDrugIndex, {
          ...existing,
          currentPage: page,
          imageUrl: existing.tiffUrls[page],
        });
      }
      return updated;
    });
  };

  const handleAttachmentChange = async (newAttachmentIndex: number) => {
    if (!currentDrugImage || !pharmacyId || !patientId) return;
    if (newAttachmentIndex < 0 || newAttachmentIndex >= currentDrugImage.allPrescriptions.length) return;

    const newPrescription = currentDrugImage.allPrescriptions[newAttachmentIndex];
    if (!newPrescription.scriptImageContentId) {
      setDrugImages((prev) => {
        const updated = new Map(prev);
        const existing = prev.get(activeDrugIndex);
        if (existing) {
          updated.set(activeDrugIndex, {
            ...existing,
            prescription: newPrescription,
            image: null,
            imageUrl: null,
            tiffUrls: [],
            currentPage: 0,
            currentAttachmentIndex: newAttachmentIndex,
            error: 'No image available for this attachment',
            isPdf: false,
          });
        }
        return updated;
      });
      return;
    }

    setDrugImages((prev) => {
      const updated = new Map(prev);
      const existing = prev.get(activeDrugIndex);
      if (existing) {
        updated.set(activeDrugIndex, {
          ...existing,
          loading: true,
          currentAttachmentIndex: newAttachmentIndex,
        });
      }
      return updated;
    });

    try {
      const image = await rxService.getRxImage(
        pharmacyId,
        patientId,
        newPrescription.scriptImageContentId,
      );

      // PDFs (Fillware scans) bypass the raster converter — render in an <iframe>.
      const isPdf = (image.mimeType || '').toLowerCase().includes('pdf');
      if (isPdf) {
        setDrugImages((prev) => {
          const updated = new Map(prev);
          const existing = prev.get(activeDrugIndex);
          if (existing) {
            updated.set(activeDrugIndex, {
              ...existing,
              prescription: newPrescription,
              image,
              imageUrl: `data:application/pdf;base64,${image.content}`,
              tiffUrls: [],
              currentPage: 0,
              loading: false,
              error: null,
              currentAttachmentIndex: newAttachmentIndex,
              isPdf: true,
            });
          }
          return updated;
        });
        return;
      }

      const prepared = await ImageConverter.prepareImageForDisplay(
        image.content,
        image.mimeType,
        image.compressionType,
      );

      let imageUrl: string | null = null;
      let tiffUrls: string[] = [];

      if (prepared.tiffUrls && prepared.tiffUrls.length > 0) {
        tiffUrls = prepared.tiffUrls;
        imageUrl = prepared.tiffUrls[0];
      } else {
        // PDFs render in an <iframe>; prefer the blob URL (see fetchDrugRxImage).
        const preferBlob = (image.mimeType || '').toLowerCase() === 'application/pdf';
        imageUrl = preferBlob
          ? (prepared.blobUrl || prepared.dataUrl || null)
          : (prepared.dataUrl || prepared.blobUrl || null);
      }

      setDrugImages((prev) => {
        const updated = new Map(prev);
        const existing = prev.get(activeDrugIndex);
        if (existing) {
          updated.set(activeDrugIndex, {
            ...existing,
            prescription: newPrescription,
            image,
            imageUrl,
            tiffUrls,
            currentPage: 0,
            loading: false,
            error: null,
            currentAttachmentIndex: newAttachmentIndex,
            isPdf: false,
          });
        }
        return updated;
      });
    } catch (error) {
      console.error('Failed to load attachment image:', error);
      setDrugImages((prev) => {
        const updated = new Map(prev);
        const existing = prev.get(activeDrugIndex);
        if (existing) {
          updated.set(activeDrugIndex, {
            ...existing,
            prescription: newPrescription,
            image: null,
            imageUrl: null,
            tiffUrls: [],
            currentPage: 0,
            loading: false,
            error: 'Failed to load attachment image',
            currentAttachmentIndex: newAttachmentIndex,
            isPdf: false,
          });
        }
        return updated;
      });
    }
  };

  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 25, 300));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 25, 25));
  const handleRotate = () => setRotation((prev) => (prev + 90) % 360);
  const handleFlip = () => setFlipped((prev) => !prev);
  const handleReset = () => {
    setZoom(100);
    setRotation(0);
    setFlipped(false);
  };

  const retry = () => {
    if (currentMedication?.rxNumber) {
      fetchDrugRxImage(activeDrugIndex, currentMedication.rxNumber);
    }
  };

  // Print the currently displayed Rx image / hard copy (GP-1267). Nurses at the
  // clinic routinely print the script image and blood requisitions, so the
  // viewer needs its own Print action. We print the exact bytes on screen: for
  // a non-PDF the rotated/flipped `displayUrl` (rotation is baked into the
  // bitmap — GP-909, so WYSIWYG); for a PDF the blob URL shown in the iframe.
  // Zoom is a screen-only transform and is intentionally NOT applied — a print
  // should be the full image, not the zoomed crop. An isolated hidden iframe
  // keeps the app's own DOM/styles out of the printout.
  const handlePrintImage = () => {
    const imageUrl = currentDrugImage?.imageUrl ?? null;
    const printUrl = isPdf ? imageUrl : (displayUrl || imageUrl);
    if (!printUrl) return;

    const rxLabel = String(
      currentDrugImage?.prescription?.rxNumber || currentMedication?.rxNumber || '',
    );

    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.position = 'fixed';
    iframe.style.left = '-9999px';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);

    const cleanup = () => {
      if (iframe.parentNode) document.body.removeChild(iframe);
    };

    const win = iframe.contentWindow;
    if (!win) {
      cleanup();
      return;
    }

    // Remove the frame once the print dialog closes; long fallback for browsers
    // that never fire `afterprint`.
    if ('onafterprint' in win) {
      const after = () => {
        win.removeEventListener('afterprint', after);
        cleanup();
      };
      win.addEventListener('afterprint', after);
    } else {
      setTimeout(cleanup, 60000);
    }

    const doPrint = () => {
      try {
        win.focus();
        win.print();
      } catch {
        cleanup();
      }
    };

    if (isPdf) {
      // Render the PDF blob in the iframe, then print it directly.
      iframe.onload = () => setTimeout(doPrint, 250);
      iframe.src = printUrl;
      return;
    }

    // Non-PDF: write a minimal document that fits the image to the printed page.
    const doc = iframe.contentDocument || win.document;
    if (!doc) {
      cleanup();
      return;
    }
    doc.open();
    doc.write(
      `<!DOCTYPE html><html><head><title>Rx ${rxLabel}</title>` +
        '<style>@page{margin:12mm}html,body{margin:0;padding:0}' +
        'img{max-width:100%;max-height:100vh;height:auto;display:block;margin:0 auto}</style>' +
        `</head><body><img src="${printUrl}" alt="Rx ${rxLabel}" /></body></html>`,
    );
    doc.close();

    const img = doc.querySelector('img');
    if (img && !img.complete) {
      img.onload = doPrint;
      img.onerror = cleanup;
    } else {
      setTimeout(doPrint, 100);
    }
  };

  return (
    <Box
      sx={{
        height: '100%',
        bgcolor: 'grey.100',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box sx={{ px: 1.5, py: 1, bgcolor: 'grey.900', color: 'white' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <ImageIcon sx={{ fontSize: 18, opacity: 0.8 }} />
            <Typography variant="subtitle2" fontWeight={600}>Rx Image</Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            {currentDrugImage?.allPrescriptions && currentDrugImage.allPrescriptions.length > 1 && (
              <Chip
                label={`${currentDrugImage.allPrescriptions.length} files`}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.75rem',
                  bgcolor: 'rgba(255,255,255,0.15)',
                  color: 'white',
                  fontWeight: 600,
                }}
              />
            )}
            {headerActions}
          </Stack>
        </Stack>
      </Box>

      {/* Attachment Tabs — shown when multiple attachments exist */}
      {currentDrugImage?.allPrescriptions && currentDrugImage.allPrescriptions.length > 1 && (
        <Box sx={{ bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={currentDrugImage.currentAttachmentIndex}
            onChange={(_, newValue) => handleAttachmentChange(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 36,
              '& .MuiTab-root': {
                minHeight: 36,
                py: 0.5,
                px: 1.5,
                fontSize: '0.75rem',
                textTransform: 'none',
              },
            }}
          >
            {currentDrugImage.allPrescriptions.map((attachment, index) => (
              <Tab
                key={index}
                label={
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Chip
                      label={
                        (labelsFromComments && humanizeAttachmentLabel(attachment.description || attachment.comment)) ||
                        getAttachmentTypeLabel(attachment.scriptImageType)
                      }
                      size="small"
                      color={attachment.scriptImageType === 1 ? 'primary' : 'default'}
                      variant={currentDrugImage.currentAttachmentIndex === index ? 'filled' : 'outlined'}
                      sx={{
                        height: 22,
                        fontSize: '0.75rem',
                        '& .MuiChip-label': { px: 1 },
                      }}
                    />
                    {attachment.scanDate && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        {format(new Date(attachment.scanDate), 'MM/dd')}
                      </Typography>
                    )}
                  </Stack>
                }
              />
            ))}
          </Tabs>
        </Box>
      )}

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {currentDrugImage?.loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <Stack spacing={1} alignItems="center">
              <CircularProgress size={32} />
              <Typography variant="caption" color="text.secondary">Loading prescription image...</Typography>
            </Stack>
          </Box>
        ) : currentDrugImage?.error ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, flex: 1, p: 3 }}>
            <ImageIcon sx={{ fontSize: 48, color: 'grey.400' }} />
            <Alert severity="warning" sx={{ width: '100%' }}>{currentDrugImage.error}</Alert>
            <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={retry}>
              Retry
            </Button>
          </Box>
        ) : currentDrugImage?.image && currentDrugImage?.prescription ? (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Info + Controls Toolbar */}
            <Box sx={{ px: 1.5, py: 0.5, bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" fontWeight={700} sx={{ fontFamily: 'monospace' }}>Rx #{currentDrugImage.prescription.rxNumber}</Typography>
                <Chip
                  label={getAttachmentTypeLabel(currentDrugImage.prescription.scriptImageType)}
                  size="small"
                  color={currentDrugImage.prescription.scriptImageType === 1 ? 'primary' : 'default'}
                  sx={{ height: 20, fontSize: '0.75rem' }}
                />
                {currentMedication?.status != null && (
                  <>
                    <RxAdjStateBadge adjState={currentMedication.rxAdjState} size="small" />
                    <RxStatusBadge status={currentMedication.status} size="small" />
                  </>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {toolbarActions}
                {/* TIFF Page Navigation */}
                {currentDrugImage.tiffUrls && currentDrugImage.tiffUrls.length > 1 && (
                  <>
                    <IconButton onClick={() => handleTiffPageChange(currentDrugImage.currentPage - 1)} disabled={currentDrugImage.currentPage === 0} size="small">
                      <PrevIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', minWidth: 28, textAlign: 'center' }}>
                      {currentDrugImage.currentPage + 1}/{currentDrugImage.tiffUrls.length}
                    </Typography>
                    <IconButton onClick={() => handleTiffPageChange(currentDrugImage.currentPage + 1)} disabled={currentDrugImage.currentPage === currentDrugImage.tiffUrls.length - 1} size="small">
                      <NextIcon fontSize="small" />
                    </IconButton>
                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                  </>
                )}
                {/* Zoom / Rotate / Flip — hidden for PDFs (the browser's PDF
                    viewer has its own controls, and CSS transforms don't apply
                    to an <iframe>). */}
                {!isPdf && (
                  <>
                    <IconButton onClick={handleZoomOut} size="small" title="Zoom Out"><ZoomOutIcon sx={{ fontSize: 18 }} /></IconButton>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', minWidth: 32, textAlign: 'center' }}>{zoom}%</Typography>
                    <IconButton onClick={handleZoomIn} size="small" title="Zoom In"><ZoomInIcon sx={{ fontSize: 18 }} /></IconButton>
                    <IconButton onClick={handleRotate} size="small" title="Rotate 90°"><RotateRightIcon sx={{ fontSize: 18 }} /></IconButton>
                    <IconButton onClick={handleFlip} size="small" title="Flip Horizontal" color={flipped ? 'primary' : 'default'}><FlipIcon sx={{ fontSize: 18 }} /></IconButton>
                    <Button size="small" onClick={handleReset} variant="text" sx={{ minWidth: 'auto', fontSize: '0.75rem', px: 0.5, ml: 0.5 }}>Reset</Button>
                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                  </>
                )}
                <IconButton size="small" onClick={handlePrintImage} title="Print Image">
                  <PrintIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton size="small" onClick={retry} title="Refresh">
                  <RefreshIcon sx={{ fontSize: 18 }} />
                </IconButton>
                {onViewFullImage && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (currentDrugImage?.prescription && currentDrugImage?.image) {
                        onViewFullImage(currentDrugImage.prescription, currentDrugImage.image);
                      }
                    }}
                    title="Full View"
                  >
                    <VisibilityIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                )}
              </Box>
            </Box>

            {/* Image Viewer — scroll + grab-to-pan. The image is centered with
                `margin: '0 auto'`, which collapses to left-align when it overflows
                so the full width (including the left edge) stays scrollable. */}
            <Box
              ref={isPdf ? undefined : pan.containerRef}
              onMouseDown={isPdf ? undefined : pan.onMouseDown}
              sx={{
                flex: 1,
                bgcolor: isPdf ? 'white' : '#1a1a2e',
                overflow: 'auto',
                p: isPdf ? 0 : 1,
                position: 'relative',
                display: 'flex',
                alignItems: isPdf ? 'stretch' : 'flex-start',
                cursor: isPdf ? 'default' : pan.cursor,
                userSelect: pan.isPanning ? 'none' : undefined,
              }}
            >
              {currentDrugImage.imageUrl ? (
                isPdf ? (
                  <iframe
                    src={currentDrugImage.imageUrl}
                    title={`Prescription PDF for Rx #${currentMedication?.rxNumber}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      minHeight: '500px',
                      border: 'none',
                      backgroundColor: 'white',
                    }}
                  />
                ) : (
                  <img
                    src={displayUrl || currentDrugImage.imageUrl}
                    alt={`Prescription for ${currentMedication?.rxNumber}`}
                    draggable={false}
                    onLoad={pan.refresh}
                    onDragStart={(e) => e.preventDefault()}
                    style={{
                      display: 'block',
                      flexShrink: 0,
                      objectFit: 'contain',
                      // `margin: auto` centers the image while it fits and
                      // collapses to start-align on overflow, keeping the leading
                      // edge scrollable/pannable in every orientation. Rotation /
                      // flip is baked into the bitmap (GP-909), so there's no CSS
                      // transform for the scroll container to miss.
                      ...(fitMode === 'height'
                        ? {
                            height: `${zoom}%`,
                            width: 'auto',
                            maxHeight: 'none',
                            margin: 'auto',
                          }
                        : {
                            width: `${zoom}%`,
                            maxWidth: 'none',
                            height: 'auto',
                            margin: '0 auto',
                          }),
                      transition: pan.isPanning ? 'none' : 'width 0.2s ease, height 0.2s ease',
                      borderRadius: 4,
                      boxShadow: '0 2px 12px rgba(0,0,0,0.4)',
                      userSelect: 'none',
                    }}
                  />
                )
              ) : (
                <Alert severity="info">Processing image...</Alert>
              )}
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 1, p: 3 }}>
            <ImageIcon sx={{ fontSize: 48, color: 'grey.300' }} />
            <Typography variant="body2" color="text.secondary">No prescription image available</Typography>
            <Typography variant="caption" color="text.disabled">Rx #{currentMedication?.rxNumber}</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default RxImagePanel;
